import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { allDayOn, berlinWall, dayBlocks, timeLabel } from '@kontor/shared';
import { toneClass, type useAppointmentTypes } from './appointmentTypes';
import { itemHref, taskMark, type CalItem } from './calendarItems';
import { PersonDot } from './PersonDot';

/** Höhe einer Stunde im Raster, in Pixeln */
const HOUR_PX = 52;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const dowShort = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });
const at = (day: string) => new Date(`${day}T12:00:00`);

interface Props {
  days: string[];
  appointments: CalItem[];
  types: ReturnType<typeof useAppointmentTypes>;
  today: string;
  /** Name zu einer Benutzer-ID, für freigegebene Termine */
  nameOf: (id: string) => string | null;
  /** Ein Tipp auf den Kopf eines Tages (in der Wochenansicht wechselt er zur Tagesansicht) */
  onPickDay?: (day: string) => void;
}

/**
 * Stundenraster für Tag und Woche (KON-070): oben die Tage mit den ganztägigen Terminen, darunter 00:00 bis 24:00 Uhr mit den Terminen als Blöcken in der Höhe ihrer Dauer.
 * Die Anordnung der Blöcke rechnet `dayBlocks` aus packages/shared. Ein freigegebener Termin eines Kollegen zeigt nur Zeit und Titel und lässt sich nicht öffnen.
 */
export function TimeGrid({ days, appointments, types, today, nameOf, onPickDay }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const byId = new Map(appointments.map((a) => [a.id, a]));
  const columns = days.map((day) => ({ day, blocks: dayBlocks(appointments, day), allDay: allDayOn(appointments, day) }));
  const earliest = Math.min(...columns.flatMap((c) => c.blocks.map((b) => b.startMin)), 7 * 60);

  // Beim Öffnen und beim Wechsel des Zeitraums auf den Arbeitsbeginn scrollen, bei früheren Terminen eine Stunde davor
  const firstDay = days[0];
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = Math.max(0, (Math.floor(earliest / 60) - 1) * HOUR_PX - 8);
    // nur beim Wechsel des Zeitraums, nicht bei jedem Nachladen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDay, days.length]);

  const nowWall = berlinWall(now);
  const nowMin = Number(nowWall.time.slice(0, 2)) * 60 + Number(nowWall.time.slice(3, 5));
  const style = { '--cols': days.length, '--hour': `${HOUR_PX}px` } as React.CSSProperties;
  const label = (a: CalItem, day: string) => (a.masked ? `${a.title}, freigegeben von ${a.sharedByUserIds.map((x) => nameOf(x) ?? 'Kollege').join(', ')}` : a.kind === 'task' ? `Aufgabe: ${a.title}${a.done ? ' (erledigt)' : ''}` : a.title) + (a.kind === 'task' ? '' : `, ${timeLabel(a, day)}`);

  return (
    <div className="tg" style={style}>
      <div className="tg__head" role="row">
        <div className="tg__corner" />
        {columns.map(({ day }) => (
          <button
            key={day}
            type="button"
            className={`tg__dayhead${day === today ? ' is-today' : ''}`}
            onClick={() => onPickDay?.(day)}
            disabled={!onPickDay}
            aria-label={new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long' }).format(at(day))}
          >
            <span className="tg__dow">{dowShort.format(at(day))}</span>
            <span className="tg__date">{Number(day.slice(8))}</span>
          </button>
        ))}
      </div>

      {columns.some((c) => c.allDay.length > 0) && (
        <div className="tg__allday">
          <div className="tg__corner tg__corner--label">ganztägig</div>
          {columns.map(({ day, allDay }) => (
            <div key={day} className="tg__allcell">
              {allDay.map((g) => {
                const a = byId.get(g.id)!;
                return a.masked ? (
                  <span key={a.id} className={`cal__event is-masked ${toneClass(undefined)}`} title={label(a, day)}><PersonDot item={a} nameOf={nameOf} />{a.title}</span>
                ) : (
                  <Link key={a.id} to={itemHref(a)} className={`cal__event ${toneClass(types.find(a.typeId)?.color)}${a.kind === 'task' ? ' cal__event--task' : ''}${a.done ? ' is-done' : ''}`} title={label(a, day)}><PersonDot item={a} nameOf={nameOf} />{taskMark(a)}{a.title}</Link>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div className="tg__scroll" ref={scroller}>
        <div className="tg__body">
          <div className="tg__axis" aria-hidden="true">
            {HOURS.map((h) => <div key={h} className="tg__hour">{h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}</div>)}
          </div>
          {columns.map(({ day, blocks }) => (
            <div key={day} className={`tg__col${day === today ? ' is-today' : ''}`} role="list" aria-label={new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long' }).format(at(day))}>
              {HOURS.map((h) => <div key={h} className="tg__line" />)}
              {day === today && <div className="tg__now" style={{ top: `${(nowMin / 60) * HOUR_PX}px` }} aria-hidden="true" />}
              {blocks.map((b) => {
                const a = byId.get(b.id)!;
                const style = {
                  top: `${(b.startMin / 60) * HOUR_PX}px`,
                  height: `${((b.endMin - b.startMin) / 60) * HOUR_PX - 2}px`,
                  left: `calc(${(b.column / b.columns) * 100}% + 1px)`,
                  width: `calc(${100 / b.columns}% - 3px)`,
                };
                // Bis 45 Minuten ist der Block unter 40 px hoch: Zeit und Titel stehen dann nebeneinander, sonst wird der Kreis der Person abgeschnitten
                const short = b.endMin - b.startMin <= 45;
                const content = (
                  <>
                    <span className="tg__time">{timeLabel(a, day).replace('–', ' – ')}</span>
                    <span className="tg__title"><PersonDot item={a} nameOf={nameOf} />{a.title}</span>
                    {a.masked && <span className="tg__who">{a.sharedByUserIds.map((x) => nameOf(x) ?? 'Kollege').join(', ')}</span>}
                  </>
                );
                return a.masked ? (
                  <div key={a.id} role="listitem" className={`tg__block is-masked${short ? ' tg__block--short' : ''}`} style={style} title={label(a, day)} aria-label={label(a, day)}>{content}</div>
                ) : (
                  <Link key={a.id} role="listitem" to={itemHref(a)} className={`tg__block ${toneClass(types.find(a.typeId)?.color)}${short ? ' tg__block--short' : ''}`} style={style} title={label(a, day)}>{content}</Link>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
